import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import ImageSelector from './imageSelector';
import '../../styles/Cooking.css';

const CustomRecipeForm = ({ visible, onHide, onSave, initialValues = {}, mealTypes }) => {
    const [customMeal, setCustomMeal] = useState({
        title: '',
        mealType: null,
        prepTime: '',
        servings: '',
        ingredients: [],
        instructions: [],
        image: '',
        ...initialValues
    });

    const [newIngredient, setNewIngredient] = useState('');
    const [newInstruction, setNewInstruction] = useState('');

    const handleSave = () => {
        onSave(customMeal);
        resetForm();
    };

    const resetForm = () => {
        setCustomMeal({
            title: '',
            mealType: null,
            prepTime: '',
            servings: '',
            ingredients: [],
            instructions: [],
            image: '',
        });
        setNewIngredient('');
        setNewInstruction('');
    };

    // Custom dialog header with close button
    const dialogHeader = (
        <div className="crf-dialog-header">
            <span>Create Custom Recipe</span>
            <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-text p-button-plain"
                onClick={() => {
                    onHide();
                    resetForm();
                }}
                aria-label="Close"
            />
        </div>
    );

    return (
        <Dialog
            header={initialValues?.title ? "Edit Recipe" : "Create Custom Recipe"}
            visible={visible}
            onHide={() => {
                onHide();
                resetForm();
            }}
            className="crf-dialog frazza-dialog-xl"
            dismissableMask={true}
            modal
            footer={(
                <>
                    <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => {
                        onHide();
                        resetForm();
                    }} />
                    <Button label="Save Recipe" icon="pi pi-check" onClick={handleSave} />
                </>
            )}
        >
            <div className="crf-form-container">
                <div className="frazza-form-group">
                    <label htmlFor="title" className="frazza-form-label required">Recipe Title</label>
                    <InputText
                        id="title"
                        value={customMeal.title}
                        onChange={(e) => setCustomMeal({ ...customMeal, title: e.target.value })}
                        className="frazza-input"
                        placeholder="Enter recipe title"
                    />
                </div>

                <div className="crf-form-grid">
                    <div className="crf-form-column crf-ingredients-section">
                        <div className="frazza-form-group">
                            <label className="frazza-form-label">Ingredients</label>
                            <div className="crf-form-inline">
                                <InputText
                                    value={newIngredient}
                                    onChange={(e) => setNewIngredient(e.target.value)}
                                    placeholder="Add an ingredient"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newIngredient.trim()) {
                                            setCustomMeal({
                                                ...customMeal,
                                                ingredients: [...customMeal.ingredients, newIngredient.trim()]
                                            });
                                            setNewIngredient('');
                                            e.preventDefault();
                                        }
                                    }}
                                    className="frazza-input"
                                />
                            <Button
                                icon="pi pi-plus"
                                className="crf-add-btn"
                                onClick={() => {
                                    if (newIngredient.trim()) {
                                        setCustomMeal({
                                            ...customMeal,
                                            ingredients: [...customMeal.ingredients, newIngredient.trim()]
                                        });
                                        setNewIngredient('');
                                    }
                                }}
                            />
                        </div>

                        <ul className="crf-item-list">
                            {customMeal.ingredients.map((item, idx) => (
                                <li key={idx} className="crf-list-item">
                                    <span className="crf-item-text">{item}</span>
                                    <Button
                                        icon="pi pi-times"
                                        className="crf-remove-btn"
                                        onClick={() => setCustomMeal({
                                            ...customMeal,
                                            ingredients: customMeal.ingredients.filter((_, i) => i !== idx)
                                        })}
                                    />
                                </li>
                            ))}
                        </ul>
                        </div>

                        <div className="frazza-form-group">
                            <label className="frazza-form-label">Instructions (Steps)</label>
                            <div className="crf-form-inline">
                                <InputTextarea
                                    value={newInstruction}
                                    onChange={(e) => setNewInstruction(e.target.value)}
                                    placeholder="Add a step"
                                    rows={2}
                                    autoResize
                                    className="frazza-textarea"
                                />
                            <Button
                                icon="pi pi-plus"
                                className="crf-add-btn"
                                onClick={() => {
                                    if (newInstruction.trim()) {
                                        setCustomMeal({
                                            ...customMeal,
                                            instructions: [...customMeal.instructions, newInstruction.trim()]
                                        });
                                        setNewInstruction('');
                                    }
                                }}
                            />
                        </div>

                        <ol className="crf-step-list">
                            {customMeal.instructions.map((step, idx) => (
                                <li key={idx} className="crf-list-item">
                                    <span className="crf-step-number">{idx + 1}.</span>
                                    <span className="crf-item-text">{step}</span>
                                    <Button
                                        icon="pi pi-times"
                                        className="crf-remove-btn"
                                        onClick={() => setCustomMeal({
                                            ...customMeal,
                                            instructions: customMeal.instructions.filter((_, i) => i !== idx)
                                        })}
                                    />
                                </li>
                            ))}
                        </ol>
                        </div>
                    </div>

                    <div className="crf-form-column crf-details-section">
                        <div className="crf-form-row">
                            <div className="frazza-form-group">
                                <label htmlFor="mealType" className="frazza-form-label">Meal Type</label>
                                <Dropdown
                                    id="mealType"
                                    value={customMeal.mealType}
                                    options={mealTypes}
                                    onChange={(e) => setCustomMeal({ ...customMeal, mealType: e.value })}
                                    placeholder="Select type"
                                    className="frazza-select"
                                />
                            </div>

                            <div className="frazza-form-group">
                                <label htmlFor="prepTime" className="frazza-form-label">Prep Time</label>
                                <InputText
                                    id="prepTime"
                                    value={customMeal.prepTime}
                                    onChange={(e) => setCustomMeal({ ...customMeal, prepTime: e.target.value })}
                                    className="frazza-input"
                                    placeholder="e.g. 30 minutes"
                                />
                            </div>

                            <div className="frazza-form-group">
                                <label htmlFor="servings" className="frazza-form-label">Servings</label>
                                <InputText
                                    id="servings"
                                    value={customMeal.servings}
                                    onChange={(e) => setCustomMeal({ ...customMeal, servings: e.target.value })}
                                    className="frazza-input"
                                    placeholder="e.g. 4"
                                />
                            </div>
                        </div>
                        <div className="frazza-form-group">
                            <label className="frazza-form-label">Recipe Image</label>
                            <ImageSelector
                                onImageSelect={(url) => setCustomMeal({ ...customMeal, image: url })}
                                selectedImage={customMeal.image}
                            />
                        </div>

                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default CustomRecipeForm;